import React from 'react';
import { colors } from '@alixpartners/ui-components';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { renderToString } from 'react-dom/server';

import {
  ChartContainer,
  TooltipContainer,
  TooltipItem,
  TooltipColor,
  TooltipItemName,
  TooltipItemValue,
} from './ApplicationChart.styles';
import { DECIMAL_TYPES, ICurrencyFormat } from '../../utils/types';
import {
  CHART_CONFIGS,
  getColorIndex,
  getFormattedPercentage,
  tooltipValueFormatter,
} from '../../utils/format';
import {
  CHART_TYPES_CONSTANTS,
  DECIMALS_FORMATS,
  DISPLAY_BY,
} from 'utils/constants';

type Serie = {
  data: Array<Number>;
  name?: string;
};

const COLORS = ['#032E45', '#0696A6'];

export interface IApplicationChartProps {
  categories: string[];
  chartWidth?: number;
  displayType: string;
  format: ICurrencyFormat;
  onClick: (c?: string) => void;
  type: string;
  series: Serie[];
  selectedCategory?: string;
  highlightedCategories?: string[];
  width?: string;
  chartRef?: any;
}

const getPlotBands = ({
  categories,
  highlightedCategories = [],
  onClick,
  selectedCategory,
}: {
  categories: string[];
  highlightedCategories?: string[];
  onClick: (c?: string) => void;
  selectedCategory?: string;
}) => {
  const selectedIndexes = categories.filter(
    (item: string) =>
      item === selectedCategory || highlightedCategories.includes(item)
  );

  return categories.map((category: string, index: number) => ({
    from: index - 0.5,
    to: index + 0.5,
    color: selectedIndexes.includes(category) ? colors.greyBg : 'transparent',
    events: {
      click: () => {
        const selection =
          selectedIndexes.includes(category) || category.indexOf('Total') === 0
            ? undefined
            : category;

        onClick(selection);
      },
    },
  }));
};

const transformSelection = (chartRef?: any) => {
  const chart = chartRef && chartRef.current && chartRef.current.chart;

  if (
    chart &&
    chart.xAxis &&
    chart.xAxis[0] &&
    chart.xAxis[0].plotLinesAndBands
  ) {
    chart.xAxis[0].plotLinesAndBands.forEach((plotBand: any) => {
      plotBand.svgElem.attr({
        transform: 'translate(0, -42) scale(1, 2.054)',
      });
    });
  }
};

export const ApplicationChart = ({
  categories,
  chartRef,
  chartWidth,
  displayType,
  format,
  highlightedCategories,
  onClick,
  series: seriesRaw,
  type,
  selectedCategory,
  width = '100%',
}: IApplicationChartProps) => {
  let maxDecimal = DECIMALS_FORMATS[format.decimal];
  const isPercentage = displayType === DISPLAY_BY.PERCENTAGE_OF_REVENUE;
  const isNumber = displayType === DISPLAY_BY.HEADCOUNT;
  if (isPercentage) {
    const maxSerieValue: number = Math.max.apply(
      null,
      seriesRaw.map((serie) => Math.max.apply(null, serie.data as number[]))
    );
    maxDecimal = maxSerieValue < 10 ? '.0000' : maxDecimal;
  }
  const tooltipBarId = 'tooltip-bar-123-' + Math.ceil(Math.random() * 1000);
  const series = seriesRaw.map((serie, index) => ({
    ...serie,
    color: COLORS[getColorIndex(serie.name || '')],
    marker: {
      symbol: 'square',
    },
  }));
  const chartOptions = {
    chart: {
      type: 'column',
      spacingTop: 40,
      className: 'bar-chart',
      width: chartWidth || null,
      style: { fontFamily: 'Roboto' },
      marginTop: 40,
      spacingBottom: 40,
      height: 500,
      animation: { duration: 200 },
      events: {
        redraw: () => {
          transformSelection(chartRef);
        },
      },
    },
    loading: {
      hideDuration: 500,
      showDuration: 500,
      labelStyle: {
        fontWeight: 'normal',
        letterSpacing: '0.3px',
      },
    },
    title: {
      text: null,
    },
    xAxis: {
      categories,
      labels: {
        autoRotation: undefined,
        style: {
          color: colors.grey7,
          fontSize: '11px',
          letterSpacing: '0.3px',
          fontWeight: 500,
        },
        // @ts-ignore
        formatter: function () {
          return `<span style="opacity: ${
            // @ts-ignore
            !selectedCategory || this.value === selectedCategory ? 1 : 0.4
            // @ts-ignore
          }">${this.value}</span>`;
        },
      },
      plotBands: getPlotBands({
        categories,
        highlightedCategories,
        onClick,
        selectedCategory,
      }),
    },
    legend: {
      align: 'right',
      verticalAlign: 'top',
      enabled: series.length === 2 && displayType !== DISPLAY_BY.HEADCOUNT,
    },
    yAxis: {
      min: 0,
      height: '100%',
      title: {
        text: null,
      },
      labels: {
        // @ts-ignore
        formatter: function () {
          if (isPercentage || isNumber) {
            // @ts-ignore
            return this.value;
          }
          // @ts-ignore
          return CHART_CONFIGS[type].labelFormatter(this.value, format);
        },
      },
    },
    credits: { enabled: false },
    exporting: { enabled: false },
    tooltip: {
      className: 'bar-chart-tooltip',
      shared: true,
      useHTML: true,
      backgroundColor: colors.white,
      borderWidth: 0,
      borderRadius: 2,

      formatter: function () {
        // @ts-ignore
        const { points } = this;
        return renderToString(
          <TooltipContainer id={tooltipBarId}>
            {points.map(
              (point: {
                series: { name: string };
                colorIndex: number;
                y: number;
              }) =>
                point ? (
                  <TooltipItem key={`${point.series.name}-${point.colorIndex}`}>
                    <span>
                      <TooltipColor
                        colorIndex={getColorIndex(point.series.name)}
                      />
                      <TooltipItemName>{point.series.name}</TooltipItemName>
                    </span>
                    <TooltipItemValue>
                      {tooltipValueFormatter({
                        value: point.y,
                        format,
                        displayType,
                        maxDecimal,
                      })}
                    </TooltipItemValue>
                  </TooltipItem>
                ) : null
            )}
          </TooltipContainer>
        );
      },
    },
    plotOptions: {
      series: {
        animation: false,
        events: {
          legendItemClick: (e: any) => {
            e.preventDefault();
          },
          // @ts-ignore
          click: function (e) {
            onClick(e.point.category);
          },
        },
        states: {
          select: { enabled: true, marker: { enabled: true } },
        },
        dataLabels: {
          enabled:
            type === CHART_TYPES_CONSTANTS.CLUSTERED_COLUMN ||
            series.length === 1,
          verticalAlign: 'top',
          y: -24,
          // @ts-ignore
          formatter: function () {
            if (isPercentage) {
              // @ts-ignore
              return getFormattedPercentage(this.y, {
                ...format,
                decimal: maxDecimal as keyof DECIMAL_TYPES,
              });
            }
            if (isNumber) {
              // @ts-ignore
              return this.y;
            }
            // @ts-ignore
            return CHART_CONFIGS[type].labelFormatter(this.y, format);
          },
          style: {
            color: 'black',
            textOutline: '0px contrast',
            'stroke-width': 0,
          },
        },
      },
      column: {
        stacking: CHART_CONFIGS[type].stacking,
      },
    },
    series,
  };
  return (
    <ChartContainer width={width}>
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
        ref={chartRef}
      />
    </ChartContainer>
  );
};

export default ApplicationChart;
